import React, { useState, useEffect } from 'react';
import './FuroManual.css';

function fromRasgo(r) {
  const dir    = r.direcao || 'Y';
  const inicio = dir === 'Y'
    ? String(r.y - r.comprimento / 2)
    : String(r.x - r.comprimento / 2);
  const final_ = dir === 'Y'
    ? String(r.y + r.comprimento / 2)
    : String(r.x + r.comprimento / 2);
  // posicao = borda inicial no eixo perpendicular
  const posicao = dir === 'Y'
    ? String(r.x - r.largura / 2)
    : String(r.y - r.largura / 2);
  return {
    face:         r.face || 'superior',
    direcao:      dir,
    inicio,
    final:        final_,
    posicao,
    largura:      String(r.largura),
    profundidade: String(r.profundidade ?? ''),
  };
}

function toRasgo(d) {
  const ini  = parseFloat(d.inicio);
  const fin  = parseFloat(d.final);
  const pos  = parseFloat(d.posicao);
  const larg = parseFloat(d.largura);
  const comp = fin - ini;
  const centerMain = (ini + fin) / 2;
  const centerPerp = pos + larg / 2;
  return {
    x:            d.direcao === 'Y' ? centerPerp : centerMain,
    y:            d.direcao === 'Y' ? centerMain : centerPerp,
    comprimento:  comp,
    largura:      larg,
    direcao:      d.direcao,
    face:         d.face,
    profundidade: parseFloat(d.profundidade),
  };
}

const EMPTY = {
  face: 'superior', direcao: 'Y',
  inicio: '', final: '', posicao: '', largura: '', profundidade: '',
};

function RasgoManual({ onSaveRasgo, rasgoEditando, onCancelEdit, pecaDimensoes, onFieldChange, rasgoKey }) {
  const [rasgoData, setRasgoData] = useState(EMPTY);
  const [errors, setErrors]       = useState({});

  useEffect(() => {
    setRasgoData(rasgoEditando ? fromRasgo(rasgoEditando) : EMPTY);
    setErrors({});
  }, [rasgoKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const editando = rasgoEditando !== null && rasgoEditando !== undefined;

  const handleChange = (campo, valor) => {
    const next = { ...rasgoData, [campo]: valor };
    setRasgoData(next);
    if (errors[campo]) setErrors({ ...errors, [campo]: null });
    if (editando && onFieldChange) {
      const ini  = parseFloat(next.inicio);
      const fin  = parseFloat(next.final);
      const pos  = parseFloat(next.posicao);
      const larg = parseFloat(next.largura);
      const prof = parseFloat(next.profundidade);
      if ([ini, fin, pos, larg, prof].every(v => !isNaN(v)) && fin > ini && larg > 0 && prof > 0) {
        onFieldChange(toRasgo(next));
      }
    }
  };

  const validar = () => {
    const erros = {};
    const ini  = parseFloat(rasgoData.inicio);
    const fin  = parseFloat(rasgoData.final);
    const pos  = parseFloat(rasgoData.posicao);
    const larg = parseFloat(rasgoData.largura);
    const prof = parseFloat(rasgoData.profundidade);

    if (isNaN(ini) || ini < 0)      erros.inicio      = 'Inválido';
    if (isNaN(fin) || fin <= ini)   erros.final       = fin <= ini ? `Deve ser > ${rasgoData.direcao}-Início` : 'Inválido';
    if (isNaN(pos) || pos < 0)      erros.posicao     = 'Inválido';
    if (isNaN(larg) || larg <= 0)   erros.largura     = 'Inválida';
    if (isNaN(prof) || prof <= 0)   erros.profundidade = 'Inválida';

    setErrors(erros);
    return Object.keys(erros).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validar()) return;
    onSaveRasgo(toRasgo(rasgoData));
    if (!editando) {
      setRasgoData(EMPTY);
      setErrors({});
    }
  };

  const dir = rasgoData.direcao;
  // limite final ao longo do eixo principal
  const limitePrincipal = dir === 'Y'
    ? (pecaDimensoes?.comprimento || '')
    : (pecaDimensoes?.largura    || '');
  // eixo perpendicular (para label de posição)
  const eixoPerp = dir === 'Y' ? 'X' : 'Y';

  return (
    <div className="furo-manual">
      <form onSubmit={handleSubmit} className="furo-manual-form">

        {/* Face + Direção */}
        <div className="rasgo-meta-row" style={{ gap: 12 }}>
          <div className="rasgo-toggle-group">
            <span className="toggle-label">Face</span>
            <div className="toggle-buttons">
              <button type="button"
                className={`toggle-btn ${dir === 'X' && rasgoData.face === 'superior' ? 'active' : rasgoData.face === 'superior' ? 'active' : ''}`}
                onClick={() => handleChange('face', 'superior')}>▲ Sup</button>
              <button type="button"
                className={`toggle-btn ${rasgoData.face === 'inferior' ? 'active' : ''}`}
                onClick={() => handleChange('face', 'inferior')}>▼ Inf</button>
            </div>
          </div>
          <div className="rasgo-toggle-group">
            <span className="toggle-label">Direção</span>
            <div className="toggle-buttons">
              <button type="button"
                className={`toggle-btn ${dir === 'X' ? 'active' : ''}`}
                onClick={() => handleChange('direcao', 'X')}>→ X</button>
              <button type="button"
                className={`toggle-btn ${dir === 'Y' ? 'active' : ''}`}
                onClick={() => handleChange('direcao', 'Y')}>↑ Y</button>
            </div>
          </div>
        </div>

        {/* Início / Final (pontas do canal) */}
        <div className="form-row-2">
          <div className="form-group">
            <label>{dir}-Início</label>
            <input type="number" step="0.1"
              value={rasgoData.inicio}
              onChange={(e) => handleChange('inicio', e.target.value)}
              placeholder="0" />
            {errors.inicio && <span className="field-error">{errors.inicio}</span>}
          </div>
          <div className="form-group">
            <label>{dir}-Final</label>
            <input type="number" step="0.1"
              value={rasgoData.final}
              onChange={(e) => handleChange('final', e.target.value)}
              placeholder={limitePrincipal ? `≤ ${limitePrincipal}` : ''} />
            {errors.final && <span className="field-error">{errors.final}</span>}
          </div>
        </div>

        {/* Posição (eixo perp.) + Largura */}
        <div className="form-row-2">
          <div className="form-group">
            <label>{eixoPerp}-Pos.</label>
            <input type="number" step="0.1"
              value={rasgoData.posicao}
              onChange={(e) => handleChange('posicao', e.target.value)}
              placeholder="0" />
            {errors.posicao && <span className="field-error">{errors.posicao}</span>}
          </div>
          <div className="form-group">
            <label>Largura</label>
            <input type="number" step="0.1"
              value={rasgoData.largura}
              onChange={(e) => handleChange('largura', e.target.value)}
              placeholder="mm" />
            {errors.largura && <span className="field-error">{errors.largura}</span>}
          </div>
        </div>

        {/* Profundidade + Botão */}
        <div className="prof-com-botao">
          <div className="form-group">
            <label>Prof.</label>
            <input type="number" step="0.1"
              value={rasgoData.profundidade}
              onChange={(e) => handleChange('profundidade', e.target.value)}
              placeholder="mm" />
            {errors.profundidade && <span className="field-error">{errors.profundidade}</span>}
          </div>
          <div className="form-group">
            <label className="label-hidden">·</label>
            <button type="submit" className="btn-add-furo">
              {editando ? 'Salvar' : 'Adicionar'}
            </button>
          </div>
          {editando && (
            <div className="form-group">
              <label className="label-hidden">·</label>
              <button type="button" className="btn-cancel-rasgo" onClick={onCancelEdit}>✕</button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default RasgoManual;
